import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../../../core/api/queries';
import { useReportPage } from '../../../shared/hooks';
import { ReportPageLayout } from '../../../shared/components/layouts';
import { EditionSelector, normalizeSchoolName } from '../../../shared/components/tables';
import { columnBuilder } from '../../../shared/utils';
import { useWasteOrdersMutations } from '../hooks/use-waste-orders-mutations';
import type { WasteCollectionFormReportDTO, WasteCollectionFormDTO } from '../api/waste-collection.api';
import { renderPhotoThumbnail } from '../../../shared/components/tables';
import { Button } from '@/components/ui/button';
import { eticheteazaComanda } from '../validation/identificator-comanda';
import { poateEditaComanda } from '../validation/editare-comanda';

// Import dialog components
import { OrderFormDialog } from '../components/OrderFormDialog';
import { LogisticsDialog } from '../components/LogisticsDialog';
import { RejectionDialog } from '@/shared/components/dialogs/RejectionDialog';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { ActionButton } from '../../../shared/components/buttons/ActionButton';

// Import hooks for user role checking
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';

/**
 * Waste Orders Report Page
 *
 * Most complex report page with 45+ columns and 3 interactive dialogs.
 * Displays waste collection orders with admin approval workflow and logistics tracking.
 *
 * **Features**:
 * - Role-based actions (admin/logistics)
 * - Order form editing dialog
 * - Logistics values dialog
 * - Approval/rejection dialog
 * - Photo and document links
 * - Edition filtering support
 * - Excel export
 *
 * **Architecture** (Refactored 2025-11-01):
 * - Uses useReportPage hook with withEditions=true (consolidates all state management)
 * - Uses ReportPageLayout component (consolidates JSX structure)
 * - Uses columnBuilder for column definitions
 * - Uses separate mutations hook (useWasteOrdersMutations)
 * - Reduced from 534 lines to ~350 lines (35% reduction, 184 lines eliminated)

 */
export function WasteOrdersReportPage() {
    // ==========================================
    // 1. DATA FETCHING & REPORT INTERFACE
    // ==========================================

    const { report, adapters, handleExport, editions, currentEdition, changeEdition } = useReportPage({
        queryHook: api.wasteOrders.report,
        initialSort: ['timestamp,desc'],
        exportEndpoint: '/api/waste-collection-forms/report',
        exportFilename: 'raport_comenzi_deseuri',
        withEditions: true  // Auto-filters by selected edition
    });

    // Filter out rejected orders (isValidated = false) - client-side filtering
    // Show only approved (isValidated = true) and pending (isValidated = null/undefined) orders
    const filteredData = useMemo(() => {
        return report.data.filter(order => order.isValidated !== false);
    }, [report.data]);

    // Create modified report object with filtered data
    const reportWithFilteredData = useMemo(() => ({
        ...report,
        data: filteredData,
        totalElements: filteredData.length
    }), [report, filteredData]);

    // ==========================================
    // 2. ROLE CHECKING & NAVIGATION
    // ==========================================

    const navigate = useNavigate();
    const { userInfo } = useAuthProviderV2();
    const { ROLES, PAGES, PATHS } = useRoutesProvider();

    const isAdmin = [ROLES.ADMIN, ROLES.DBEST].includes(userInfo?.role as any);
    const isLogistic = userInfo?.role === ROLES.LOGISTIC;
    const isProfesor = userInfo?.role === ROLES.PROFESOR;

    // ==========================================
    // 3. MUTATIONS & DIALOG STATE
    // ==========================================

    const mutations = useWasteOrdersMutations();

    // Dialog state management (3 separate dialogs)
    const [selectedOrder, setSelectedOrder] = useState<WasteCollectionFormReportDTO | null>(null);
    const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
    const [isLogisticsDialogOpen, setIsLogisticsDialogOpen] = useState(false);
    const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
    const [orderDeProcesat, setOrderDeProcesat] = useState<WasteCollectionFormReportDTO | null>(null);

    // Dialog handlers
    const handleOpenOrderDialog = useCallback((order: WasteCollectionFormReportDTO) => {
        setSelectedOrder(order);
        setIsOrderDialogOpen(true);
    }, []);

    const handleOpenLogisticsDialog = useCallback((order: WasteCollectionFormReportDTO) => {
        setSelectedOrder(order);
        setIsLogisticsDialogOpen(true);
    }, []);

    const handleOpenApprovalDialog = useCallback((order: WasteCollectionFormReportDTO) => {
        setSelectedOrder(order);
        setIsApprovalDialogOpen(true);
    }, []);

    const handleSaveOrder = useCallback(async (data: Partial<WasteCollectionFormDTO>) => {
        if (!selectedOrder?.id) {
            toast.error('Eroare: ID comandă lipsește');
            return;
        }

        const fullData: WasteCollectionFormDTO = {
            id: selectedOrder.id,
            schoolId: selectedOrder.school?.id,
            contactPersonName: data.contactPersonName || selectedOrder.contactPersonName,
            contactPersonEmail: data.contactPersonEmail || selectedOrder.contactPersonEmail,
            contactPersonPhone: data.contactPersonPhone || selectedOrder.contactPersonPhone,
            deeeKg: data.deeeKg ?? selectedOrder.deeeKg,
            dbaKg: data.dbaKg ?? selectedOrder.dbaKg,
            becuriSiNeoaneKg: data.becuriSiNeoaneKg ?? selectedOrder.becuriSiNeoaneKg,
            pickupAddress: data.pickupAddress || selectedOrder.pickupAddress,
            pickupAddressSameWithSchoolAddress: data.pickupAddressSameWithSchoolAddress ?? selectedOrder.pickupAddressSameWithSchoolAddress,
            dataOrientativaColectare: selectedOrder.dataOrientativaColectare,
            photos: selectedOrder.photos,
            consent: true,
        } as WasteCollectionFormDTO;

        await mutations.updateOrder.mutateAsync(fullData);
        setIsOrderDialogOpen(false);
        setSelectedOrder(null);
    }, [selectedOrder, mutations.updateOrder]);

    const handleSaveLogistics = useCallback(async (data: {
        realDeeeKg: number;
        realDbaKg: number;
        realBecuriSiNeoaneKg: number;
        deeeOrderDocument?: string;
        dbaOrderDocument?: string;
    }) => {
        if (!selectedOrder?.id) return;
        const proceseVerbale: string[] = [];
        if (data.deeeOrderDocument) proceseVerbale.push(data.deeeOrderDocument);
        if (data.dbaOrderDocument) proceseVerbale.push(data.dbaOrderDocument);

        await mutations.updateLogistics.mutateAsync({
            orderId: selectedOrder.id,
            data: {
                realDeeeKg: data.realDeeeKg,
                realDbaKg: data.realDbaKg,
                realBecuriSiNeoaneKg: data.realBecuriSiNeoaneKg,
                proceseVerbale
            }
        });
        setIsLogisticsDialogOpen(false);
        setSelectedOrder(null);
    }, [selectedOrder, mutations.updateLogistics]);

    const handleRejectOrder = useCallback(async (reason: string) => {
        if (!selectedOrder?.id) return;
        await mutations.rejectOrder.mutateAsync({ orderId: selectedOrder.id, reason });
        setIsApprovalDialogOpen(false);
        setSelectedOrder(null);
    }, [selectedOrder, mutations.rejectOrder]);

    const handleApproveOrder = useCallback(async (order: WasteCollectionFormReportDTO) => {
        if (!order.id) return;
        await mutations.approveOrder.mutateAsync(order.id);
    }, [mutations.approveOrder]);

    // Marcarea e definitiva din ecran (butonul dispare dupa ea), deci cere confirmare: pe 08.10
    // butonul, etichetat doar «Procesată», a fost apasat de cineva care cauta formularul comenzii.
    // Eroarea o arata deja `onError`-ul mutatiei; aici doar tinem dialogul deschis pentru reincercare.
    const handleConfirmProcessed = useCallback(async () => {
        if (!orderDeProcesat?.id) return;
        try {
            await mutations.markAsProcessed.mutateAsync(orderDeProcesat.id);
            setOrderDeProcesat(null);
        } catch {
            // ramane deschis
        }
    }, [orderDeProcesat, mutations.markAsProcessed]);

    // ==========================================
    // 4. COLUMN DEFINITIONS (45+ columns)
    // ==========================================

    const columns = useMemo(() =>
        columnBuilder<WasteCollectionFormReportDTO>()
            // ===== ADMIN ACTION COLUMN (conditional) =====
            .when(isAdmin, (b) => b
                .action('actions_admin', 'Acțiuni Admin', (_, row) => (
                    <div className="flex gap-2">
                        {/* O comanda decisa nu se mai editeaza, dar trebuie sa poata fi VAZUTA:
                            acelasi dialog, in modul doar-citire. */}
                        <ActionButton
                            intent="edit"
                            onClick={() => handleOpenOrderDialog(row)}
                            disabled={mutations.isLoading}
                            title={poateEditaComanda(row.isValidated) ? 'Editare comandă' : 'Vizualizare comandă'}
                        >
                            {poateEditaComanda(row.isValidated) ? 'Editare' : 'Vizualizare'}
                        </ActionButton>
                        {!row.isValidated && (
                            <>
                                <ActionButton
                                    intent="approve"
                                    onClick={() => handleApproveOrder(row)}
                                    disabled={mutations.isLoading}
                                    title="Validare comandă"
                                >
                                    Validare
                                </ActionButton>
                                <ActionButton
                                    intent="reject"
                                    onClick={() => handleOpenApprovalDialog(row)}
                                    disabled={mutations.isLoading}
                                    title="Respingere comandă"
                                >
                                    Respingere
                                </ActionButton>
                            </>
                        )}
                        {row.isValidated && !row.isProcessed && (
                            <ActionButton
                                intent="process"
                                onClick={() => setOrderDeProcesat(row)}
                                disabled={mutations.isLoading}
                                title="Marchează comanda ca procesată"
                                className="whitespace-nowrap"
                            >
                                Marchează procesată
                            </ActionButton>
                        )}
                        {/* Starea, ca text simplu — sa nu mai semene cu un buton. */}
                        {row.isValidated && row.isProcessed && (
                            <span className="inline-flex items-center whitespace-nowrap text-small font-medium text-dark">
                                Procesată
                            </span>
                        )}
                    </div>
                ))
            )

            // ===== LOGISTIC ACTION COLUMN (conditional) =====
            .when(isLogistic, (b) => b
                .action('actions_logistics', 'Acțiuni Logistică', (_, row) => (
                    <ActionButton
                        intent="delete"
                        onClick={() => handleOpenLogisticsDialog(row)}
                        disabled={mutations.isLoading || !row.isValidated}
                        title={!row.isValidated ? "Comandă nevalidată" : "Actualizare valori reale"}
                    >
                        Valori Reale
                    </ActionButton>
                ))
            )

            // ===== PHOTOS & DOCUMENTS (5 columns) =====
            .custom({
                key: 'photos[0]',
                label: 'Foto 1',
                sortable: false,
                filterable: false,
                render: (_, row) => renderPhotoThumbnail(row.photos?.[0])
            })
            .custom({
                key: 'photos[1]',
                label: 'Foto 2',
                sortable: false,
                filterable: false,
                render: (_, row) => renderPhotoThumbnail(row.photos?.[1])
            })
            .custom({
                key: 'photos[2]',
                label: 'Foto 3',
                sortable: false,
                filterable: false,
                render: (_, row) => renderPhotoThumbnail(row.photos?.[2])
            })
            .document('deeeOrderDocument', 'Document DEEE')
            .document('dbaOrderDocument', 'Document DBA')

            // ===== EDITION & TIMESTAMPS (6 columns) =====
            .text('editionId', 'Editia', undefined, { filterable: false })
            .timestamp('timestamp', 'Data plasare comanda')
            .timestamp('validatedAt', 'Data validare')
            .timestamp('deeeOrderProcessedDate', 'Data procesare DEEE')
            .timestamp('dbaOrderProcessedDate', 'Data procesare DBA')
            .timestamp('dataOrientativaColectare', 'Data orientativa colectare')

            // ===== SCHOOL IDENTITY (5 columns) =====
            .nested('school.codSiiirUnitate', 'Cod SIIIR', (row) => row.school?.codSiiirUnitate, {
                filterPlaceholder: 'Cod SIIIR...',
                filterType: 'number'
            })
            .text('deeeOrderNumber', 'Cod comanda DEEE', 'Cod comanda DEEE...')
            .text('dbaOrderNumber', 'Cod comanda DBA', 'Cod comanda DBA...')
            // Titlul randului in rezumatul de pe mobil, normalizat doar la afisare.
            .nested('school.numeInstitutieInvatamant', 'Denumire instituție învățământ', (row) => normalizeSchoolName(row.school?.numeInstitutieInvatamant), {
                cardRole: 'title',
                filterPlaceholder: 'Denumire instituție...'
            })
            .nested('school.codSe', 'Cod SE', (row) => row.school?.codSe, {
                filterPlaceholder: 'Cod SE...'
            })
            .custom({
                key: 'addressDifferent',
                label: 'Adresă diferită de cea a școlii',
                sortable: false,
                filterable: false,
                render: (_, row) => {
                    // Compare school address with pickup address
                    const schoolAddr = row.school?.adresa;
                    const pickupAddr = row.pickupAddress;

                    if (!schoolAddr || !pickupAddr) return 'N/A';

                    // Helper to normalize empty values (null, undefined, empty string)
                    const normalize = (val: string | null | undefined) =>
                        (val === null || val === undefined || val === '') ? '' : val.trim();

                    // Compare all address fields (excluding id)
                    const isDifferent =
                        normalize(schoolAddr.judet) !== normalize(pickupAddr.judet) ||
                        normalize(schoolAddr.oras) !== normalize(pickupAddr.oras) ||
                        normalize(schoolAddr.comuna) !== normalize(pickupAddr.comuna) ||
                        normalize(schoolAddr.sat) !== normalize(pickupAddr.sat) ||
                        normalize(schoolAddr.strada) !== normalize(pickupAddr.strada) ||
                        normalize(schoolAddr.numar) !== normalize(pickupAddr.numar) ||
                        normalize(schoolAddr.codPostal) !== normalize(pickupAddr.codPostal) ||
                        normalize(schoolAddr.bloc) !== normalize(pickupAddr.bloc) ||
                        normalize(schoolAddr.scara) !== normalize(pickupAddr.scara) ||
                        normalize(schoolAddr.etaj) !== normalize(pickupAddr.etaj) ||
                        normalize(schoolAddr.apartament) !== normalize(pickupAddr.apartament);

                    return isDifferent ? <span className="font-bold">DA</span> : '-';
                }
            })
            .nested('school.cui', 'CUI', (row) => row.school?.cui, {
                filterPlaceholder: 'CUI...'
            })

            // ===== PICKUP ADDRESS (11 columns) =====
            .nested('pickupAddress.judet', 'Județ adresă colectare', (row) => row.pickupAddress?.judet, {
                filterPlaceholder: 'Județ...'
            })
            .nested('pickupAddress.oras', 'Oras adresa colectare', (row) => row.pickupAddress?.oras, {
                filterPlaceholder: 'Oras...'
            })
            .nested('pickupAddress.comuna', 'Comuna adresa colectare', (row) => row.pickupAddress?.comuna, {
                filterPlaceholder: 'Comuna...'
            })
            .nested('pickupAddress.sat', 'Sat adresa colectare', (row) => row.pickupAddress?.sat, {
                filterPlaceholder: 'Sat...'
            })
            .nested('pickupAddress.strada', 'Strada adresa colectare', (row) => row.pickupAddress?.strada, {
                filterPlaceholder: 'Strada...'
            })
            .nested('pickupAddress.numar', 'Număr adresă colectare', (row) => row.pickupAddress?.numar, {
                filterPlaceholder: 'Număr...'
            })
            .nested('pickupAddress.bloc', 'Bloc adresa colectare', (row) => row.pickupAddress?.bloc, {
                filterPlaceholder: 'Bloc...'
            })
            .nested('pickupAddress.scara', 'Scara bloc adresa colectare', (row) => row.pickupAddress?.scara, {
                filterPlaceholder: 'Scara...'
            })
            .nested('pickupAddress.etaj', 'Etaj bloc adresa colectare', (row) => row.pickupAddress?.etaj, {
                filterPlaceholder: 'Etaj...'
            })
            .nested('pickupAddress.apartament', 'Apartament adresa colectare', (row) => row.pickupAddress?.apartament, {
                filterPlaceholder: 'Apartament...'
            })
            .nested('pickupAddress.codPostal', 'Cod postal adresa colectare', (row) => row.pickupAddress?.codPostal, {
                filterPlaceholder: 'Cod postal...'
            })

            // ===== CONTACT INFO (3 columns) =====
            .text('contactPersonName', 'Nume contact', 'Nume contact...')
            .text('contactPersonPhone', 'Telefon contact', 'Telefon contact...')
            .text('contactPersonEmail', 'Email contact', 'Email contact...')

            // ===== WEIGHT ESTIMATES (3 columns) =====
            .number('deeeKg', 'Kilograme DEEE estimate', 'Kg DEEE...')
            .number('dbaKg', 'Kilograme DBA estimate', 'Kg DBA...')
            .number('becuriSiNeoaneKg', 'Kilograme BECURI si NEOANE estimate', 'Kg BECURI...')

            // ===== WEIGHT FINALS (3 columns) =====
            .number('realDeeeKg', 'Kilograme DEEE finale', 'Kg DEEE finale...')
            .number('realDbaKg', 'Kilograme DBA finale', 'Kg DBA finale...')
            .number('realBecuriSiNeoaneKg', 'Kilograme BECURI si NEOANE finale', 'Kg BECURI finale...')

            // ===== STATUS (3 columns) =====
            .boolean('isValidated', 'Validat', 'DA/NU')
            .text('rejectReason', 'Motiv respingere', undefined, { filterable: false })
            .boolean('isProcessed', 'Comanda transmisa', 'DA/NU')

            .build(),
        [isAdmin, isLogistic, mutations.isLoading, handleOpenOrderDialog, handleOpenLogisticsDialog, handleOpenApprovalDialog, handleApproveOrder]
    );

    // ==========================================
    // 5. RENDER
    // ==========================================

    return (
        <>
            <ReportPageLayout
                title="Raport Comenzi"
                report={reportWithFilteredData}
                adapters={adapters}
                handleExport={handleExport}
                columns={columns}
                entityLabel="comenzi"
                extraControls={
                    <div className="flex gap-4 items-center">
                        {isProfesor && (
                            <Button
                                onClick={() => navigate(PATHS[PAGES.WASTE_COLLECTION_FORM])}
                                className="w-full sm:w-auto"
                            >
                                Plasare Comandă Nouă
                            </Button>
                        )}
                        {isAdmin && (
                            <EditionSelector
                                editions={editions || []}
                                currentEditionId={currentEdition?.id}
                                onChange={changeEdition || (() => {})}
                                disabled={report.loading}
                            />
                        )}
                    </div>
                }
            />

            {/* Dialogs */}
            {selectedOrder && (
                <>
                    <OrderFormDialog
                        isOpen={isOrderDialogOpen}
                        onClose={() => {
                            setIsOrderDialogOpen(false);
                            setSelectedOrder(null);
                        }}
                        onSave={handleSaveOrder}
                        orderData={selectedOrder}
                        loading={mutations.isUpdating}
                        readOnly={!poateEditaComanda(selectedOrder.isValidated)}
                    />

                    <LogisticsDialog
                        isOpen={isLogisticsDialogOpen}
                        onClose={() => {
                            setIsLogisticsDialogOpen(false);
                            setSelectedOrder(null);
                        }}
                        onSave={handleSaveLogistics}
                        orderData={selectedOrder}
                        loading={mutations.isUpdatingLogistics}
                    />

                    <RejectionDialog
                        isOpen={isApprovalDialogOpen}
                        onClose={() => {
                            setIsApprovalDialogOpen(false);
                            setSelectedOrder(null);
                        }}
                        onReject={handleRejectOrder}
                        loading={mutations.isRejecting}
                        title="Respingere Comandă"
                        contextInfo={[
                            { label: 'Comandă', value: eticheteazaComanda(selectedOrder) },
                            { label: 'Școală', value: selectedOrder.school?.numeInstitutieInvatamant || 'N/A' }
                        ]}
                        warningMessage="Comanda va fi marcată ca respinsă și școala va fi notificată."
                        rejectButtonText="Respinge Comandă"
                    />
                </>
            )}

            <ConfirmationDialog
                isOpen={orderDeProcesat !== null}
                onClose={() => setOrderDeProcesat(null)}
                onConfirm={handleConfirmProcessed}
                title="Marchează comanda ca procesată"
                description={`${orderDeProcesat ? eticheteazaComanda(orderDeProcesat) : 'Comanda'}, de la ${orderDeProcesat?.school?.numeInstitutieInvatamant ?? 'școală'} va fi marcată ca procesată. Marcarea nu poate fi anulată din platformă.`}
                confirmText="Marchează procesată"
                cancelText="Renunță"
                loading={mutations.isMarkingProcessed}
            />
        </>
    );
}
